import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { RecommendationStrategy } from 'lumen/models';

import styles from './strategy-card.module.css';

interface SettingsRecommendationsRoomHierarchyStrategyCardSignature {
  Args: {
    saveStrategyTask: TaskForAsyncTaskFunction<
      { drop: boolean },
      (
        strategyRecord: RecommendationStrategy,
      ) => Promise<{ isSuccess: boolean } | null>
    >;
    strategy: RecommendationStrategy;
    title: string;
  };
  Blocks: {
    default: [boolean];
  };
}

export default class SettingsRecommendationsRoomHierarchyStrategyCard extends Component<SettingsRecommendationsRoomHierarchyStrategyCardSignature> {
  @service declare access: Services['access'];

  @tracked isEditMode = false;

  get isSaveButtonDisabled(): boolean {
    return (
      this.args.saveStrategyTask.isRunning ||
      !this.args.strategy.hasDirtyAttributes
    );
  }

  willDestroy(): void {
    super.willDestroy();

    if (!this.args.strategy.isDestroyed && !this.args.strategy.isDestroying) {
      this.args.strategy.rollbackAttributes();
    }
  }

  @action
  disableEditMode(): void {
    this.isEditMode = false;
  }

  @action
  enableEditMode(): void {
    this.isEditMode = true;
  }

  @action
  onCancelClick(): void {
    this.args.strategy.rollbackAttributes();

    this.disableEditMode();
  }

  @action
  async onSaveClick(): Promise<void> {
    if (!this.args.strategy) {
      return;
    }

    const result = await this.args.saveStrategyTask.perform(this.args.strategy);

    if (!result?.isSuccess) {
      return;
    }

    this.disableEditMode();
  }

  <template>
    <div class={{styles.card}}>
      {{#if this.isEditMode}}
        <div class="{{styles.card-header-wrapper}} items-center gap-200">
          <h3>{{@title}}</h3>
          <div class="flex flex-row gap-200">
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{@saveStrategyTask.isRunning}}
              @label={{t "general.cancel"}}
              @onClick={{this.onCancelClick}}
              data-test-button="Cancel"
            />
            <PrismButton
              @isDisabled={{this.isSaveButtonDisabled}}
              @isLoading={{@saveStrategyTask.isRunning}}
              @label={{t "general.save"}}
              @onClick={{this.onSaveClick}}
              data-test-button="Save"
            />
          </div>
        </div>
      {{else}}
        <div class="{{styles.card-header-wrapper}} items-center gap-200">
          <h3>{{@title}}</h3>
          {{#unless
            this.access.isPricingAssistantRecommendationSettingsActionOrInputDisabled
          }}
            <PrismButton
              @buttonType="ghost"
              @icon="edit-pencil"
              @onClick={{this.enableEditMode}}
              data-test-button="Enable edit mode"
            />
          {{/unless}}
        </div>
      {{/if}}
      <div class="flex-1 pr-400 pb-400 pl-400">
        {{yield this.isEditMode}}
      </div>
    </div>
  </template>
}
